// FILE: /src/Components/Apps/CaptionMediaSelector.js

import React, { useMemo, useRef, useState } from "react";
import { Button, Empty, Input, Spin, Tooltip, message } from "antd";
import {
  CloudUploadOutlined,
  LinkOutlined,
  PictureOutlined,
  PlusOutlined,
  ReloadOutlined,
  DeleteOutlined,
} from "@ant-design/icons";

const SOURCE_TYPES = {
  UPLOAD: "upload",
  LIBRARY: "library",
  URL: "url",
};

const LIBRARY_PAGE_SIZE = 16;

const isVideoUrl = (value = "") => {
  return /\.(mp4|mov|webm)(\?|$)/i.test(String(value || "").toLowerCase());
};

const isSupportedMediaAsset = (asset) => {
  const name = String(asset?.name || "").toLowerCase();
  const url = String(asset?.url || "").toLowerCase();

  return (
    /\.(png|jpg|jpeg|webp|gif|mp4|mov|webm)$/i.test(name) ||
    /\.(png|jpg|jpeg|webp|gif|mp4|mov|webm)(\?|$)/i.test(url)
  );
};

const SourceButton = ({ active, icon, label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex items-center justify-center gap-2 rounded-xl px-3.5 py-2 text-sm font-semibold border transition-all ${
      active
        ? "bg-purple-50 text-purple-700 border-purple-200 shadow-sm"
        : "bg-white text-gray-500 border-gray-200 hover:border-purple-200 hover:text-purple-700 hover:bg-purple-50/50"
    }`}
  >
    {icon}
    {label}
  </button>
);

const SelectedMediaChip = ({ item, onRemove }) => {
  const isVideo = item.file?.type?.startsWith("video/") || isVideoUrl(item.previewUrl || item.imageUrl || item.name);

  return (
    <div className="relative w-16 h-16 rounded-xl border border-gray-200 overflow-hidden bg-gray-100">
      {isVideo ? (
        <div className="w-full h-full bg-black flex items-center justify-center">
          <video
            src={item.previewUrl}
            className="w-full h-full object-cover opacity-80"
            muted
            playsInline
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black/20">
            <i className="fa-solid fa-play text-white text-sm"></i>
          </div>
        </div>
      ) : (
        <img
          src={item.previewUrl}
          alt={item.name || "Selected"}
          className="w-full h-full object-cover"
        />
      )}

      <button
        type="button"
        onClick={() => onRemove(item.id)}
        className="absolute top-1 right-1 w-5 h-5 rounded-full bg-white/90 text-red-500 border border-red-100 flex items-center justify-center shadow-sm"
      >
        <DeleteOutlined className="text-[10px]" />
      </button>
    </div>
  );
};

const CaptionMediaSelector = ({
  assets = [],
  libraryStatus,
  selectedMedia,
  setSelectedMedia,
  onRefreshLibrary,
  maxItems = 10,
}) => {
  const fileInputRef = useRef(null);

  const [sourceType, setSourceType] = useState(SOURCE_TYPES.UPLOAD);
  const [manualUrl, setManualUrl] = useState("");
  const [libraryPage, setLibraryPage] = useState(1);
  const [isDragging, setIsDragging] = useState(false);

    const mediaAssets = useMemo(() => {
    return (assets || []).filter(isSupportedMediaAsset);
  }, [assets]);

    const totalLibraryPages = Math.max(
    Math.ceil(mediaAssets.length / LIBRARY_PAGE_SIZE),
    1
  );

  const visibleLibraryAssets = useMemo(() => {
    const start = (libraryPage - 1) * LIBRARY_PAGE_SIZE;
    return mediaAssets.slice(start, start + LIBRARY_PAGE_SIZE);
  }, [mediaAssets, libraryPage]);

  const canAddMore = selectedMedia.length < maxItems;

  const addMediaItems = (items) => {
    setSelectedMedia((prev) => {
      const existingKeys = new Set(
        prev.map((item) => item.imageUrl || item.previewUrl || item.name)
      );

      const filtered = items.filter((item) => {
        const key = item.imageUrl || item.previewUrl || item.name;
        return key && !existingKeys.has(key);
      });

      const next = [...prev, ...filtered].slice(0, maxItems);

      if (prev.length + filtered.length > maxItems) {
        message.warning(`You can select up to ${maxItems} images.`);
      }

      return next;
    });
  };

  const handleFiles = (fileList) => {
    const files = Array.from(fileList || []);

    if (!files.length) return;

    if (!canAddMore) {
      message.warning(`You can select up to ${maxItems} images.`);
      return;
    }

    const supportedFiles = files.filter((file) => {
      return file.type?.startsWith("image/") || file.type?.startsWith("video/");
    });

    if (supportedFiles.length !== files.length) {
      message.warning("Only image and video files were added.");
    }

    const items = supportedFiles.map((file) => ({
      id: `upload_${Date.now()}_${Math.random().toString(36).slice(2)}`,
      source: SOURCE_TYPES.UPLOAD,
      file,
      previewUrl: URL.createObjectURL(file),
      name: file.name,
    }));

    addMediaItems(items);
    setSourceType(SOURCE_TYPES.UPLOAD);
  };

  const handleFileInput = (event) => {
    handleFiles(event.target.files);
    event.target.value = "";
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    handleFiles(event.dataTransfer.files);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleLibrarySelect = (asset) => {
    const alreadySelected = selectedMedia.some(
      (item) => item.source === SOURCE_TYPES.LIBRARY && item.imageUrl === asset.url
    );

    if (alreadySelected) {
      removeMediaByUrl(asset.url);
      return;
    }

    if (!canAddMore) {
      message.warning(`You can select up to ${maxItems} images.`);
      return;
    }

    addMediaItems([
      {
        id: `library_${Date.now()}_${Math.random().toString(36).slice(2)}`,
        source: SOURCE_TYPES.LIBRARY,
        imageUrl: asset.url,
        previewUrl: asset.url,
        name: asset.name || "Library image",
      },
    ]);
  };

  const handleAddUrl = () => {
    const url = manualUrl.trim();

    if (!url) {
      message.warning("Please enter an image URL.");
      return;
    }

    if (!canAddMore) {
      message.warning(`You can select up to ${maxItems} images.`);
      return;
    }

    addMediaItems([
      {
        id: `url_${Date.now()}_${Math.random().toString(36).slice(2)}`,
        source: SOURCE_TYPES.URL,
        imageUrl: url,
        previewUrl: url,
        name: "URL image",
      },
    ]);

    setManualUrl("");
  };

  const removeMedia = (id) => {
    setSelectedMedia((prev) => {
      const item = prev.find((current) => current.id === id);

      if (item?.previewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(item.previewUrl);
      }

      return prev.filter((current) => current.id !== id);
    });
  };

  const removeMediaByUrl = (url) => {
    setSelectedMedia((prev) => prev.filter((item) => item.imageUrl !== url));
  };

  const clearAll = () => {
    selectedMedia.forEach((item) => {
      if (item.previewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(item.previewUrl);
      }
    });

    setSelectedMedia([]);
  };

  return (
    <div className="h-full min-h-0 flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
        <SourceButton
          active={sourceType === SOURCE_TYPES.UPLOAD}
          icon={<CloudUploadOutlined />}
          label="Upload"
          onClick={() => setSourceType(SOURCE_TYPES.UPLOAD)}
        />

        <SourceButton
          active={sourceType === SOURCE_TYPES.LIBRARY}
          icon={<PictureOutlined />}
          label="Library"
          onClick={() => setSourceType(SOURCE_TYPES.LIBRARY)}
        />

        <SourceButton
          active={sourceType === SOURCE_TYPES.URL}
          icon={<LinkOutlined />}
          label="URL"
          onClick={() => setSourceType(SOURCE_TYPES.URL)}
        />
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-2xl p-3 flex-1 min-h-0 overflow-hidden">
        {sourceType === SOURCE_TYPES.UPLOAD && (
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={`h-full min-h-[220px] rounded-2xl border-2 border-dashed flex flex-col items-center justify-center text-center transition-all ${
              isDragging
                ? "border-purple-400 bg-purple-50"
                : "border-gray-300 bg-white hover:bg-purple-50/40 hover:border-purple-300"
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
                            accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime"
              multiple
              className="hidden"
              onChange={handleFileInput}
            />

            <div className="w-14 h-14 rounded-full bg-gray-50 border border-gray-200 shadow-sm flex items-center justify-center mb-3">
              <CloudUploadOutlined className="text-3xl text-gray-400" />
            </div>

            <h2 className="text-lg font-extrabold text-gray-900 mb-1">
              Drop images here
            </h2>

              <p className="text-xs text-gray-500 mb-4">
                Optional. Use images or videos for media-aware captions.
              </p>

            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => fileInputRef.current?.click()}
            >
              Upload Images
            </Button>
          </div>
        )}

        {sourceType === SOURCE_TYPES.LIBRARY && (
          <div className="h-full min-h-0 flex flex-col">
            <div className="flex items-center justify-between gap-3 mb-3 flex-shrink-0">
              <div>
                <h2 className="text-sm font-bold text-gray-900 mb-0">
                  Media Library
                </h2>
                                <p className="text-xs text-gray-500 mb-0">
                  Showing images and videos only.
                </p>
              </div>

              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={onRefreshLibrary}
              >
                Refresh
              </Button>
            </div>

            {libraryStatus === "loading" ? (
              <div className="flex justify-center items-center flex-1">
                <Spin />
              </div>
            ) : mediaAssets.length === 0 ? (
              <div className="bg-white border border-dashed rounded-2xl flex-1 flex items-center justify-center">
                <Empty description="No image or video assets found." />
              </div>
            ) : (
              <>
                <div className="grid grid-cols-4 gap-2 overflow-y-auto pr-1 flex-1 min-h-0">
                  {visibleLibraryAssets.map((asset) => {
                    const selected = selectedMedia.some(
                      (item) =>
                        item.source === SOURCE_TYPES.LIBRARY &&
                        item.imageUrl === asset.url
                    );

                    return (
                      <button
                        key={asset.url}
                        type="button"
                        onClick={() => handleLibrarySelect(asset)}
                        title={asset.name}
                        className={`relative aspect-square rounded-xl overflow-hidden border bg-gray-100 transition-all ${
                          selected
                            ? "border-purple-600 ring-2 ring-purple-200 shadow-md"
                            : "border-gray-200 hover:border-purple-300 hover:shadow-sm"
                        }`}
                      >
                                                {isVideoUrl(asset.name || asset.url) ? (
                          <>
                            <video
                              src={asset.url}
                              className="w-full h-full object-cover bg-black"
                              muted
                              playsInline
                              preload="metadata"
                            />
                            <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                              <i className="fa-solid fa-play text-white text-lg"></i>
                            </div>
                            <div className="absolute bottom-1 left-1 bg-black/70 text-white text-[9px] px-1.5 py-0.5 rounded">
                              VIDEO
                            </div>
                          </>
                        ) : (
                          <img
                            src={asset.url}
                            alt={asset.name || "Library asset"}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        )}

                        {selected && (
                          <div className="absolute inset-0 bg-purple-950/35 flex items-center justify-center">
                            <i className="fa-solid fa-circle-check text-white text-xl"></i>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>

                <div className="mt-3 flex items-center justify-between gap-2 flex-shrink-0">
                  <p className="text-xs text-gray-500 mb-0">
                    {mediaAssets.length === 0
                      ? "0"
                      : `${(libraryPage - 1) * LIBRARY_PAGE_SIZE + 1}-${Math.min(
                          libraryPage * LIBRARY_PAGE_SIZE,
                          mediaAssets.length
                        )}`}{" "}
                    of {mediaAssets.length}
                  </p>

                  <div className="flex items-center gap-2">
                    <Button
                      size="small"
                      disabled={libraryPage <= 1}
                      onClick={() => setLibraryPage((prev) => Math.max(prev - 1, 1))}
                    >
                      Prev
                    </Button>

                    <span className="text-xs text-gray-500">
                      {libraryPage}/{totalLibraryPages}
                    </span>

                    <Button
                      size="small"
                      disabled={libraryPage >= totalLibraryPages}
                      onClick={() =>
                        setLibraryPage((prev) =>
                          Math.min(prev + 1, totalLibraryPages)
                        )
                      }
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {sourceType === SOURCE_TYPES.URL && (
          <div className="h-full min-h-[220px] flex items-center justify-center">
            <div className="w-full">
              <div className="text-center mb-4">
                <div className="w-12 h-12 rounded-2xl bg-white border border-gray-200 shadow-sm mx-auto flex items-center justify-center mb-3">
                  <LinkOutlined className="text-xl text-purple-600" />
                </div>

                <h2 className="text-lg font-extrabold text-gray-900 mb-1">
                  Paste image URL
                </h2>

                <p className="text-xs text-gray-500 mb-0">
                  Public image URL only.
                </p>
              </div>

              <div className="flex gap-2">
                <Input
                  size="large"
                  value={manualUrl}
                  onChange={(event) => setManualUrl(event.target.value)}
                  placeholder="https://example.com/image.jpg"
                  className="rounded-xl"
                  onPressEnter={handleAddUrl}
                />

                <Button
                  type="primary"
                  size="large"
                  icon={<PlusOutlined />}
                  onClick={handleAddUrl}
                >
                  Add
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl p-3 flex-shrink-0">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-bold text-gray-900 mb-0">
            Selected Media
          </h3>

          {selectedMedia.length > 0 && (
            <Button size="small" danger onClick={clearAll}>
              Clear
            </Button>
          )}
        </div>

        {selectedMedia.length === 0 ? (
          <p className="text-xs text-gray-500 mb-0">
            No media selected. You can still generate from text only.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {selectedMedia.map((item) => (
              <Tooltip key={item.id} title={item.name || item.imageUrl}>
                <div>
                  <SelectedMediaChip item={item} onRemove={removeMedia} />
                </div>
              </Tooltip>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CaptionMediaSelector;